import React, { useRef, useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import SearchIconMui from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ClearIcon from '@mui/icons-material/Clear';
import Badge from '@mui/material/Badge';
import { getRelatedTo, isFilterExist } from '#component/general/list-filter/helper';
import { SEARCH } from '#component/general/list-filter/constants';
import { useUpdateURLParams } from '#component/general/list-filter/hooks/useUpdateURLParams';
import { useUpdateLocalStorageFilterParam, useDebounce } from '#component/general/list-filter/hooks';
import {
  SearchPaper,
  SearchIcon,
  SearchErrorText,
  SearchInput,
  ClearSearchButton,
  FilterOpenButton,
  FilterOpenButtonActive
} from '#component/general/list-filter/components/general/styles';
import { useEffectOnce } from '#hooks';

export const Search = ({
  open,
  label,
  name,
  updateFilterData,
  fetchFilterData,
  resetPagination,
  handleUpdateInitState,
  setFilterAppliedStatus,
  setModalOpen,
  dispatch,
  showFilterButton
}) => {
  const inputRef = useRef();
  const prevSearch = useRef();
  const [error, setError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const relatedTo = getRelatedTo();
  const filter = useSelector(s => s.listFilter?.[relatedTo], shallowEqual);
  const filterExist = isFilterExist(filter, [SEARCH]);
  const searchValue = filter?.search?.value;
  const updateURL = useUpdateURLParams();
  const updateLocalStorage = useUpdateLocalStorageFilterParam();

  useEffectOnce(() => setInputVal(searchValue), searchValue?.length > 0);

  const debouncedUpdateFilterData = useDebounce(value => {
    dispatch(setFilterAppliedStatus(false));
    dispatch(updateFilterData(value, name, relatedTo));
  }, 150);

  const handleFilterModal = () => {
    setModalOpen(!open);
  };

  const handleChange = val => {
    updateURL({ [name]: val });
    updateLocalStorage({ value: val, name, id: relatedTo });

    dispatch(updateFilterData(inputVal, name, relatedTo));
  };

  const handleFetchFilterData = () => {
    resetPagination();
    dispatch(fetchFilterData());
    handleUpdateInitState();
    dispatch(setFilterAppliedStatus(true));
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();

      const trimmedInputVal = inputVal.trim();
      setInputVal(trimmedInputVal);

      if (!trimmedInputVal) {
        return;
      }

      if (trimmedInputVal.length && prevSearch.current === trimmedInputVal) {
        return;
      }

      if (error) {
        setShowError(true);
      } else {
        handleChange(trimmedInputVal);
        handleFetchFilterData();
        prevSearch.current = trimmedInputVal;
      }

      setModalOpen(false);
    }
  };

  const clearSearch = () => {
    setInputVal('');
    prevSearch.current = '';

    updateURL({ [name]: '' });
    updateLocalStorage({ value: '', name, id: relatedTo });
    dispatch(updateFilterData('', name, relatedTo));
  };

  const handleClearSearch = () => {
    clearSearch();
    handleFetchFilterData();
  };

  useEffect(() => {
    if (typeof inputVal === 'string' && /^.{1}$/.test(inputVal)) {
      setError(true);
    }

    return () => {
      setError(false);
    };
  }, [inputVal]);

  const handleChangeVal = e => {
    const newValue = e.target.value;

    if (!newValue.trim().length) {
      debouncedUpdateFilterData.cancel();

      setInputVal('');
      clearSearch();
      dispatch(updateFilterData('', name, relatedTo));

      if (filter?.search?.value?.trim()) {
        handleFetchFilterData();
      }
      return;
    }

    setInputVal(newValue);
    debouncedUpdateFilterData(newValue);
  };

  const FilterIconButton = open ? FilterOpenButtonActive : FilterOpenButton;

  return (
    <SearchPaper
      component="form"
      id="list-search-parent"
      onSubmit={e => e.preventDefault()}
    >
      <SearchIcon>
        <SearchIconMui />
      </SearchIcon>
      <SearchInput
        placeholder={label}
        inputProps={{ 'aria-label': 'search', id: 'list-search', autoComplete: 'off' }}
        onChange={handleChangeVal}
        inputRef={inputRef}
        onKeyDown={handleKeyDown}
        value={inputVal}
      />
      {inputVal && (
        <ClearSearchButton
          onClick={handleClearSearch}
          aria-label="Clear search"
          size="small"
        >
          <ClearIcon fontSize="small" />
        </ClearSearchButton>
      )}
      {error && showError && (
        <SearchErrorText>
          Enter 2 chars for searching
        </SearchErrorText>
      )}
      {showFilterButton && (
        <FilterIconButton
          aria-label="Button to open the filter"
          onClick={handleFilterModal}
        >
          <Badge
            id="list-search-badge"
            color="secondary"
            variant="dot"
            invisible={!filterExist}
          >
            <TuneIcon id="list-search-filter-icon" />
          </Badge>
        </FilterIconButton>
      )}
    </SearchPaper>
  );
};
